import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { getDomain } from "../../utils/constants";

const StartCheckin: React.FC = () => {
  const { appointmentId } = useParams<{ appointmentId: string }>();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!appointmentId) {
      setError("Missing appointment id in the link.");
      return;
    }

    // Call the API - get redirect URL from Location header to avoid CORS issues
    const apiUrl = `${getDomain()}/api/athena/digital-checkin/start-checkin/${encodeURIComponent(appointmentId)}`;

    let cancelled = false;

    // Use fetch with redirect: 'manual' to get the Location header without CORS issues
    fetch(apiUrl, {
      method: 'GET',
      headers: {
        'client': 'axis',
        'domain': 'axis',
      },
      redirect: 'manual', // Don't follow redirect automatically to avoid CORS
    })
      .then(async (response) => {
        if (cancelled) return;

        // Happy path: server wanted to redirect us. With `redirect: 'manual'`,
        // a 3xx surfaces as an opaqueredirect with the Location header
        // exposed; with a followed redirect it surfaces as a normal response
        // whose URL is the destination.
        const redirectUrl = response.headers.get('Location');
        if (redirectUrl) {
          window.location.href = redirectUrl;
          return;
        }

        // If the server returned a normal 2xx with no Location, prefer the
        // resolved response.url over the original API path.
        if (response.ok || response.type === 'opaqueredirect') {
          window.location.href = response.url;
          return;
        }

        // Error path: 4xx / 5xx. Parse our standard error envelope
        // ({ message, errorCode, ... }) and surface it instead of redirecting
        // back to the API URL (which would dump raw JSON on the patient).
        let errorMessage = `We couldn't start your check-in (status ${response.status}). Please try again or contact support.`;
        try {
          const body = await response.json();
          if (body && typeof body.message === 'string' && body.message.trim()) {
            errorMessage = body.message;
          }
        } catch {
          // body wasn't JSON — keep the default message.
        }
        setError(errorMessage);
      })
      .catch((err) => {
        if (cancelled) return;
        // Network error / CORS / fetch threw — surface a generic message so
        // the spinner doesn't run forever.
        console.error('Failed to start check-in', err);
        setError("We couldn't reach the check-in service. Please check your connection and try again.");
      });

    return () => {
      cancelled = true;
    };
  }, [appointmentId]);

  return (
    <div className="min-h-screen bg-[#F7F8FD] flex flex-col">
      <div className="flex flex-row px-10 py-6 shadow justify-between bg-white items-center">
        <span className="text-[#01545D] font-semibold text-2xl">
          Axis Integrated Mental Health
        </span>
        <span className="text-[#01545D] font-medium text-xl">Patient Check-In</span>
      </div>
      <main className="flex-grow container mx-auto flex flex-col items-center justify-center px-4 py-10">
        {error ? (
          <div className="flex flex-col items-center max-w-xl text-center">
            <div className="text-3xl font-semibold text-[#01545D] mb-3">
              We couldn't start your check-in
            </div>
            <div className="text-base text-[#1C1C1C] mb-6">{error}</div>
            <button
              onClick={() => window.location.reload()}
              className="px-6 py-3 bg-[#01545D] text-white rounded-full font-semibold"
            >
              Try Again
            </button>
          </div>
        ) : (
          <div className="flex flex-col items-center">
            <div className="text-4xl font-semibold text-[#01545D] text-center mb-3">
              Starting Check-In
            </div>
            <div className="text-xl text-[#1C1C1C] text-center mb-6">
              Please wait while we prepare your check-in...
            </div>
            <div className="text-sm text-[#5D6A85] mb-2">Loading...</div>
            <div className="mt-4">
              <div className="animate-spin rounded-full h-12 w-12 border-t-4 border-b-4 border-[#01545D]"></div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default StartCheckin;
